// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useNavTabs from "@/hooks/useNavTabs";
import useOrganization from "@/hooks/useOrganization";
import useTranslation from "@/hooks/useTranslation";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { onClose: () => void };

/**
 * Hangs under the chip that opened it. The person's own row goes to their page; below the divider are
 * the two actions that belong to them rather than to the company.
 *
 * Signing out closes the popup first — it lives in a portal, so the redirect behind it would otherwise
 * leave the menu floating over the login screen.
 */
export default function ProfilePopup({ onClose }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();
    const client = useAuth((state) => state.client);
    const logout = useAuth((state) => state.logout);
    const role = useOrganization((state) => state.role);
    const clearOrganization = useOrganization((state) => state.clearOrganization);
    const { settingsTab } = useNavTabs();

    const go = (path: string) => {
        onClose();
        navigate(path);
    };

    const handleLogout = () => {
        onClose();
        clearOrganization();
        logout();
    };

    return (
        <section className="popup-card profile-popup">
            <button type="button" className="profile-popup__identity" onClick={() => go("/profile")}>
                <Avatar name={client?.name ?? ""} picture={client?.picture} size={36} bordered />
                <span className="profile-popup__text">
                    <span className="profile-popup__name">{client?.name}</span>
                    <span className="profile-popup__role">{role ? t.roles[role] : ""}</span>
                </span>
            </button>

            <span className="profile-popup__divider" />

            <div className="profile-popup__actions">
                {settingsTab && (
                    <button type="button" className="profile-popup__action" onClick={() => go(settingsTab.path)}>
                        <Icon name="settings" size={18} />
                        <span>{settingsTab.label}</span>
                    </button>
                )}

                <button type="button" className="profile-popup__action profile-popup__action--logout" onClick={handleLogout}>
                    <Icon name="logout" size={18} />
                    <span>{t.common.logout}</span>
                </button>
            </div>
        </section>
    );
}
